import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";

const EMPTY = {
  plan_name: "",
  slug: "",
  description: "",
  monthly_price: 0,
  yearly_price: 0,
  included_features: [],
  max_products: 0,
  max_sellers: 0,
  max_orders_per_month: 0,
  max_storage_mb: 0,
  is_active: true,
  sort_order: 0,
};

export default function SuperAdminPlans() {
  const [rows, setRows] = useState([]);
  const [allFeatures, setAllFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [plans, feats] = await Promise.all([
        api.get("/superadmin/plans"),
        api.get("/superadmin/features"),
      ]);
      setRows(plans.data);
      setAllFeatures(feats.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r); };
  const close = () => setEditing(null);

  const toggleFeature = (key) => {
    setForm((f) => {
      const has = f.included_features.includes(key);
      return { ...f, included_features: has ? f.included_features.filter((k) => k !== key) : [...f.included_features, key] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/superadmin/plans", form);
        toast.success("Plan created");
      } else {
        await api.put(`/superadmin/plans/${form.slug}`, form);
        toast.success("Plan updated");
      }
      close(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete plan "${slug}"?`)) return;
    try {
      await api.delete(`/superadmin/plans/${slug}`);
      toast.success("Plan deleted"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="super-plans-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl">Subscription Plans</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Plans bundle a set of core features and limits. Add-ons are sold separately on top.</p>
        </div>
        <button onClick={openNew} className="kb-btn-primary inline-flex items-center gap-2" data-testid="plan-new-btn">
          <Plus size={14} /> New plan
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-48 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {rows.map((p) => (
            <div key={p.slug} className="bg-white border border-[hsl(var(--kb-border))] p-5 flex flex-col" data-testid={`plan-card-${p.slug}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-2xl">{p.plan_name}</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{p.slug}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)} className="text-[hsl(var(--kb-primary))]" data-testid={`plan-edit-${p.slug}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(p.slug)} className="text-red-600" data-testid={`plan-delete-${p.slug}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="mt-3 text-sm text-[hsl(var(--kb-muted))] line-clamp-2">{p.description || "—"}</div>
              <div className="mt-4 space-y-1 text-sm">
                <div>₹{p.monthly_price}/mo  ·  ₹{p.yearly_price}/yr</div>
                <div>{(p.included_features || []).length} included features</div>
                <div className="text-xs text-[hsl(var(--kb-muted))]">
                  {p.max_products} products · {p.max_sellers} sellers · {p.max_orders_per_month} orders/mo · {p.max_storage_mb}MB
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="plan-dialog">
            <h2 className="font-display text-2xl">{editing === "new" ? "New plan" : "Edit plan"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Name <span className="text-red-500">*</span></label>
                <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.plan_name}
                  onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                  data-testid="plan-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Slug <span className="text-red-500">*</span></label>
                <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
                  value={form.slug} readOnly={editing !== "new"}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                  data-testid="plan-slug-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Description</label>
                <textarea rows={2} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Monthly price (₹)</label>
                <input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.monthly_price}
                  onChange={(e) => setForm({ ...form, monthly_price: Number(e.target.value) })} />
              </div>
              <div>
                <label className="text-sm font-medium">Yearly price (₹)</label>
                <input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.yearly_price}
                  onChange={(e) => setForm({ ...form, yearly_price: Number(e.target.value) })} />
              </div>
              <div><label className="text-sm font-medium">Max products</label><input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1" value={form.max_products} onChange={(e) => setForm({ ...form, max_products: Number(e.target.value) })} /></div>
              <div><label className="text-sm font-medium">Max sellers</label><input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1" value={form.max_sellers} onChange={(e) => setForm({ ...form, max_sellers: Number(e.target.value) })} /></div>
              <div><label className="text-sm font-medium">Max orders / month</label><input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1" value={form.max_orders_per_month} onChange={(e) => setForm({ ...form, max_orders_per_month: Number(e.target.value) })} /></div>
              <div><label className="text-sm font-medium">Max storage (MB)</label><input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1" value={form.max_storage_mb} onChange={(e) => setForm({ ...form, max_storage_mb: Number(e.target.value) })} /></div>
              <div><label className="text-sm font-medium">Sort order</label><input type="number" className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
            </div>

            <div>
              <label className="text-sm font-medium">Included features</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {allFeatures.map((f) => (
                  <label key={f.feature_key} className="flex items-start gap-2 border border-[hsl(var(--kb-border))] p-2 text-sm cursor-pointer">
                    <input type="checkbox"
                      checked={form.included_features.includes(f.feature_key)}
                      onChange={() => toggleFeature(f.feature_key)}
                      data-testid={`plan-feature-${f.feature_key}`} />
                    <div>
                      <div className="font-medium">{f.feature_name}</div>
                      <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">{f.feature_key}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-3 border-t border-[hsl(var(--kb-border))]">
              <button className="kb-btn-outline" onClick={close}>Cancel</button>
              <button className="kb-btn-primary" onClick={save} disabled={saving || !form.plan_name || !form.slug} data-testid="plan-save-btn">
                {saving ? "Saving…" : "Save plan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
